<template>
  <section
    id="pricing"
    class="odin-software-band"
    aria-labelledby="odin-pricing-title"
  >
    <div class="odin-section container-wrapper">
      <div class="section-heading">
        <div>
          <p class="odin-eyebrow">ODIN SOFTWARE / TAILORED IMPLEMENTATION</p>
          <h2 id="odin-pricing-title">
            Choose your starting point.<br />We’ll tailor the scope.
          </h2>
        </div>
        <p class="section-intro">
          Every setup is different. Pricing depends on your websites, content
          volume, integrations and automation requirements. Tell us what you
          need, and we’ll prepare a tailored proposal.
        </p>
      </div>
      <div class="odin-plans">
        <article
          v-for="plan in plans"
          :key="plan.name"
          class="odin-plan"
          :class="{ 'is-featured': plan.featured }"
        >
          <p class="plan-kicker">
            {{ plan.featured ? "FOR MULTI-SITE OPERATORS" : "ODIN SOFTWARE" }}
          </p>
          <h3>{{ plan.name }}</h3>
          <p class="plan-price">{{ plan.price }}</p>
          <p class="plan-description">{{ plan.description }}</p>
          <p class="plan-scope-label">Scope to discuss</p>
          <ul>
            <li v-for="feature in plan.features" :key="feature">
              {{ feature }}
            </li>
          </ul>
          <button
            class="green-btn odin-button"
            type="button"
            :aria-expanded="Boolean(selectedPlan) && form.plan === plan.name"
            aria-controls="odin-software-request"
            :aria-label="plan.button + ' — ' + plan.name"
            @click="choosePlan(plan)"
          >
            {{ plan.button }} <span aria-hidden="true">↗</span>
          </button>
        </article>
      </div>
      <div class="odin-pricing-notes">
        <p>
          Starting prices for tailored engagements, not fixed-price packages or
          standard software subscriptions. Larger workloads and more complex
          requirements cost more. No monthly page volume is guaranteed.
        </p>
        <p>
          Each proposal specifies the software modules, setup, access
          arrangements and any ongoing ARI-operated services. Pilot+ starts from
          £2,900 per month. For Launch and Growth, scope, final pricing and
          payment terms are agreed in the individual proposal before work
          begins.
        </p>
      </div>
      <div class="odin-get-started-heading">
        <p class="odin-eyebrow">HOW TO GET STARTED</p>
        <h3>Tell us what you need.</h3>
        <p>
          We’ll agree the scope, pricing and setup with you before
          implementation.
        </p>
      </div>
      <div class="odin-onboarding">
        <div>
          <span>01 / YOUR REQUEST</span>
          <h3>Share your scope.</h3>
          <p>
            Tell us about your website and the work you want ODIN to support.
            Your selected starting point accompanies the enquiry.
          </p>
        </div>
        <div>
          <span>02 / YOUR PROPOSAL</span>
          <h3>Agree the setup.</h3>
          <p>
            ARI discusses the included modules, integrations, price and payment
            terms, plus the installation location, access method and delivery
            schedule.
          </p>
        </div>
        <div>
          <span>03 / IMPLEMENTATION</span>
          <h3>Set the work in motion.</h3>
          <p>
            Implementation follows the accepted proposal and any agreed
            prerequisites. Access is provided according to that setup; sending
            this form does not create an account.
          </p>
        </div>
      </div>
      <div
        id="odin-software-request"
        class="odin-software-order"
        v-show="selectedPlan"
      >
        <div>
          <p class="odin-eyebrow">YOUR SOFTWARE REQUEST</p>
          <h3 ref="orderTitle" tabindex="-1">
            Let’s set up<br />your ODIN project.
          </h3>
          <p>
            Tell us what you need. We’ll agree the scope, pricing and setup with
            you before implementation.
          </p>
          <p class="software-request-summary" v-if="activePlan">
            <strong>{{ activePlan.name }}</strong
            ><span>{{ activePlan.price }}</span>
          </p>
          <p>
            Your proposal will set out where ODIN is installed, how you access
            it and the delivery schedule. No robot is required or included.
          </p>
        </div>
        <form class="odin-request odin-software-form" @submit.prevent="submit">
          <div class="form-eyebrow">SOFTWARE ORDER ENQUIRY <span>↗</span></div>
          <p class="software-selected-plan">
            Starting point: <strong>{{ form.plan }}</strong> ·
            <a href="#pricing">Change</a>
          </p>
          <div class="form-row">
            <div class="form-field">
              <label for="odin-software-name"
                >Full name <span>required</span></label
              ><input
                id="odin-software-name"
                v-model.trim="form.name"
                autocomplete="name"
                maxlength="100"
                required
                :disabled="sending || accepted"
              />
            </div>
            <div class="form-field">
              <label for="odin-software-email"
                >Email <span>required</span></label
              ><input
                id="odin-software-email"
                v-model.trim="form.email"
                type="email"
                autocomplete="email"
                maxlength="254"
                required
                :disabled="sending || accepted"
              />
            </div>
          </div>
          <div class="form-field">
            <label for="odin-software-website"
              >Website <span>optional</span></label
            ><input
              id="odin-software-website"
              v-model.trim="form.website"
              autocomplete="url"
              maxlength="500"
              :disabled="sending || accepted"
            />
          </div>
          <div class="form-field">
            <label for="odin-software-message"
              >Required scope <span>required</span></label
            ><textarea
              id="odin-software-message"
              v-model.trim="form.message"
              rows="4"
              maxlength="2000"
              required
              :disabled="sending || accepted"
              placeholder="Goals, scope, timeline and any budget requirements…"
            ></textarea>
          </div>
          <div class="odin-trap" aria-hidden="true">
            <label for="odin-software-company">Leave empty</label
            ><input
              id="odin-software-company"
              v-model="form.company"
              tabindex="-1"
              autocomplete="off"
            />
          </div>
          <p class="form-privacy">
            Sending this enquiry does not take payment, activate access or
            reserve a robot. ARI will contact you to discuss your requirements
            and proposal.
          </p>
          <p class="form-privacy">
            ARI uses these details to respond to your project request. This does
            not subscribe you to a newsletter.
            <a href="/privacy-policy">Privacy policy ↗</a>
          </p>
          <button
            class="green-btn odin-button"
            type="submit"
            :disabled="sending || accepted"
          >
            {{
              sending
                ? "Sending software request…"
                : accepted
                ? "Software request received ✓"
                : "Send software request"
            }}
            <span v-if="!sending && !accepted" aria-hidden="true">↗</span>
          </button>
          <div
            v-if="feedback"
            ref="feedback"
            class="form-feedback"
            :class="{ 'is-error': !accepted }"
            :role="accepted ? 'status' : 'alert'"
            tabindex="-1"
          >
            {{ feedback }}
          </div>
          <div v-if="fallback" class="email-fallback">
            <a :href="mailto">Request software by email ↗</a>
            <p>
              Opens a draft with your selected plan and brief. Review and send
              it in your email app; opening the draft sends nothing.
            </p>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
<script>
const { plans } = require("./software-plans");
const {
  validateSoftwareRequest,
  submitSoftwareRequest,
  softwareMailto,
} = require("./software-request");
export default {
  name: "OdinSoftware",
  data() {
    return {
      plans,
      selectedPlan: "",
      form: {
        plan: "",
        email: "",
        name: "",
        website: "",
        message: "",
        company: "",
      },
      sending: false,
      accepted: false,
      feedback: "",
      fallback: false,
      requestId: "",
      started: Date.now(),
    };
  },
  computed: {
    activePlan() {
      return this.plans.find((p) => p.name === this.form.plan);
    },
    mailto() {
      return softwareMailto(this.form);
    },
  },
  methods: {
    async choosePlan(plan) {
      if (this.sending || this.accepted) {
        this.$refs.feedback && this.$refs.feedback.focus();
        return;
      }
      this.selectedPlan = plan.name;
      this.form.plan = plan.name;
      await this.$nextTick();
      this.$refs.orderTitle.focus({ preventScroll: true });
      this.$el.querySelector("#odin-software-request").scrollIntoView({
        block: "start",
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    },
    async submit() {
      if (this.sending || this.accepted) return;
      const invalid = validateSoftwareRequest(this.form);
      if (invalid) {
        this.feedback = invalid;
        return;
      }
      this.sending = true;
      this.feedback = "";
      this.fallback = false;
      if (!this.requestId)
        this.requestId = window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (b) =>
              b.toString(16).padStart(2, "0")
            ).join("");
      try {
        const endpoint =
          process.env.VUE_APP_ODIN_SOFTWARE_API_URL ||
          (["ari.company", "www.ari.company"].includes(location.hostname)
            ? "https://api.ari.company/api/odin/software-request"
            : "/api/odin/software-request");
        const result = await submitSoftwareRequest(
          {
            ...this.form,
            request_id: this.requestId,
            elapsed_ms: Date.now() - this.started,
          },
          endpoint
        );
        this.accepted = result.accepted;
        this.feedback = result.message;
        this.fallback = !result.accepted;
      } catch (e) {
        this.feedback =
          "We could not confirm that your software request was received. Please contact ARI before sending another request.";
        this.fallback = true;
      } finally {
        this.sending = false;
        this.$nextTick(() => {
          if (this.$refs.feedback) this.$refs.feedback.focus();
        });
      }
    },
  },
};
</script>
